/* eslint-disable arrow-body-style */

import React, { useRef, useState, useEffect } from 'react';
import { getPDFFileByNode } from '@gp/utils/common';
import { getCaptureNodeFile } from '@gp/utils/file';
import { handleCaptureNodeClone } from '../../../v1/utils';
import { DOWNLOAD_PNG_TYPE } from '../../../constants';

import type { IDPSReportBaseVO } from '@gp/types/dpsReport/common';

const MAX_REPEAT_COUNT = 3;

function useGenerateReportFileSilentCommon(generator: () => Promise<File>) {
  const repeatCount = useRef(0);

  const file = useRef<File | null>(null);

  const [loading, setLoading] = useState(false);

  const generateFile = async () => {
    try {
      setLoading(true);
      const res = await generator();
      file.current = res;
      setLoading(false);
    } catch (exception) {
      console.warn('generate file silent: ', exception);
      if (repeatCount.current >= MAX_REPEAT_COUNT) {
        setLoading(false);
        return;
      }
      repeatCount.current = repeatCount.current + 1;
      generateFile();
    }
  };

  const resetGenerateFile = () => {
    repeatCount.current = 0;
    file.current = null;
    setLoading(false);
  };

  return { loading, file, generateFile, resetGenerateFile };
}

export default function useGenerateReportFileSilent(props: {
  node: React.RefObject<HTMLDivElement | null>;
  fileName: string;
  report?: IDPSReportBaseVO | null;
}) {
  const { node, fileName, report } = props;

  const generated = useRef(false);

  const {
    loading: imageLoading,
    file: imageFile,
    generateFile: generateImage,
    resetGenerateFile: resetGenerateImage,
  } = useGenerateReportFileSilentCommon(() => {
    return getCaptureNodeFile({
      type: DOWNLOAD_PNG_TYPE,
      node: node.current as any,
      fileName,
      options: {
        /**
         * 这里是因为在可编辑的dps周报截图的时候，html2canvas这玩意儿读取到node节点的宽度偏小，所以在onclone回调内 强制纠正
         * html2canvas这玩意儿 不知道什么原因 会丢失 width、height、padding这些样式，需要在onclone回调里 再赋值一遍，不然下载下来的东西 跟 页面展示的不一样
         */
        onclone: (document: Document, element: HTMLElement) => {
          if (!node.current || !element) return;
          if (report?.type === '1') {
            handleCaptureNodeClone(node, element);
          }
        },
      },
    });
  });

  const {
    loading: pdfLoading,
    file: pdfFile,
    generateFile: generatePDF,
    resetGenerateFile: resetGeneratePDF,
  } = useGenerateReportFileSilentCommon(() => {
    return getPDFFileByNode({
      node: node.current as any,
      fileName,
      html2canvasOptions: {},
      jsPDFOptions: {},
    });
  });

  const startGenerate = async () => {
    if (generated.current) return;
    await Promise.allSettled([generateImage(), generatePDF()]);
    generated.current = true;
  };

  const resetGenerate = () => {
    resetGenerateImage();
    resetGeneratePDF();
    generated.current = false;
  };

  useEffect(() => {
    resetGenerate();
  }, [report]);

  return { loading: imageLoading || pdfLoading, imageFile, pdfFile, startGenerate };
}
